import { useContext, useEffect } from 'react';

import { SignalRContext } from 'shared/contexts/signalr';
import { useToasts } from 'shared/hooks/useToasts';
import { Toast } from 'shared/typings/toasts';

interface AdPayload {
  id: string;
  name: string;
  preview: string; // Stringified HTML IFrame of Ad Preview
}

interface SuccessPayload {
  ads: AdPayload[];
}

interface FacebookError extends Error {
  message: string;
  error_user_title: string;
  error_user_msg: string;
  estimated_time_to_regain_access?: string;
  // custom fields for ad errors
  failed_creative_id?: string;
  failed_ad_name?: string;
}

interface AdErrorPayload {
  errors: FacebookError[];
}

interface CreativeErrorPayload {
  error: FacebookError;
}

const formatErrorToastPayload = (error: FacebookError): Toast => {
  const {
    message,
    error_user_msg,
    error_user_title,
    estimated_time_to_regain_access,
    failed_creative_id,
    failed_ad_name,
  } = error;
  let title;
  if (error_user_title) title = `Title: ${error_user_title}\n`;
  let detailedMessage = `Message: ${message}`;
  if (error_user_msg) detailedMessage += `\nDetailed Message: ${error_user_msg}`;

  if (estimated_time_to_regain_access) {
    detailedMessage += `\nEstimated time to regain access: ${estimated_time_to_regain_access}`;
  }
  if (failed_creative_id) {
    detailedMessage += `\nFailed Creative ID: ${failed_creative_id}`;
  }

  if (failed_ad_name) {
    detailedMessage += `\nFailed Ad Name: ${failed_ad_name}`;
  }
  return {
    message: detailedMessage,
    heading: title,
  };
};

export const useFacebookAdNotification = (): void => {
  const { connection } = useContext(SignalRContext);
  const { setErrorToast, setSuccessToast } = useToasts();
  useEffect(() => {
    const adHandler = ({ ads }: SuccessPayload) => {
      setSuccessToast({
        message: `The following Facebook Ads have been created: [${ads.map(({ name }) => name).join(', ')}]`,
      });
    };

    const adErrorHandler = ({ errors }: AdErrorPayload) => {
      errors.forEach((error) => {
        setErrorToast(formatErrorToastPayload(error));
      });
    };

    const creativeErrorHandler = ({ error }: CreativeErrorPayload) => {
      setErrorToast(formatErrorToastPayload(error));
    };

    const adCopyErrorHandler = ({ errors }) => {
      errors.forEach((error) => {
        setErrorToast({ message: error });
      });
    };

    if (connection) {
      connection.on('newFacebookAd', adHandler);
      connection.on('errorFacebookAd', adErrorHandler);
      connection.on('errorFacebookCreative', creativeErrorHandler);
      connection.on('errorFacebookCopy', adCopyErrorHandler);
    }

    return function cleanup() {
      connection?.off('newFacebookAd', adHandler);
      connection?.off('errorFacebookAd', adErrorHandler);
      connection?.off('errorFacebookCreative', creativeErrorHandler);
      connection?.off('errorFacebookCopy', adCopyErrorHandler);
    };
  }, [connection]);
};
